import React from "react";
import PropTypes from "prop-types";
import { useCalibrationReport } from "./useCalibrationReport";
import "../../components/ui-components/DataTable.css";

// Human-readable label for each range source the backend emits.
// Mirrors RANGE_LABEL_TEXT in ConfidenceBadge.jsx (not exported there).
const RANGE_LABEL_TEXT = {
  calibrated: "90% range",
  recent_errors: "range of recent errors",
  model: "model range",
  calibrating: "calibrating",
};

const pct = (v) => (v == null ? "—" : `${Math.round(v * 100)}%`);

// signedPct renders a signed percentage for bias values (e.g. +6%, −3%).
const signedPct = (v) => {
  if (v == null) return "—";
  const rounded = Math.round(v * 100);
  return rounded >= 0 ? `+${rounded}%` : `${rounded}%`;
};

// CalibrationPanel renders the calibration report: a total card + per-budget table.
// When a `report` prop is provided it is used directly (pure render / test mode).
// When omitted the component fetches the endpoint itself.
const CalibrationPanel = ({ report: reportProp }) => {
  const fetchResult = useCalibrationReport();

  // Use the prop when supplied (test / parent-fetch mode), otherwise use the query.
  const report = reportProp !== undefined ? reportProp : fetchResult.data;
  const isLoading = reportProp === undefined && fetchResult.isLoading;
  const isError = reportProp === undefined && fetchResult.isError;

  if (isLoading) {
    return (
      <div className="space-y-2">
        <div className="h-24 animate-pulse rounded-card bg-surface-2" />
        <div className="h-32 animate-pulse rounded-card bg-surface-2" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-card border border-danger bg-danger-soft px-4 py-3 text-sm text-danger">
        Could not load calibration report. Please try again shortly.
      </div>
    );
  }

  if (!report) return null;

  const total = report.total || {};
  const conformal = total.conformal || {};
  const band = total.band || {};
  const mape = total.projection_mape || {};
  const budgets = report.budgets || [];

  const noHistory = conformal.n === 0;
  const v3 = report.v3;
  const adaptive = report.adaptive;

  return (
    <div className="space-y-3">
      {/* Total card */}
      <div className="rounded-card border border-card-bd bg-card px-5 py-4 text-sm">
        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-3">
          Calibration — last {report.window_months || 6} months
        </div>

        {noHistory ? (
          <p className="text-text-2">
            Not enough history — calibrating. Check back once the first month closes.
          </p>
        ) : (
          <dl className="grid grid-cols-1 gap-y-1 sm:grid-cols-2">
            <div>
              <dt className="inline text-text-3">Conformal interval </dt>
              <dd className="inline font-tabular text-text">
                covered {pct(conformal.coverage)} (n={conformal.n})
              </dd>
              {conformal.label ? (
                <span className="ml-2 text-xs text-text-2">
                  · {RANGE_LABEL_TEXT[conformal.label] || conformal.label}
                </span>
              ) : null}
            </div>

            {band.n > 0 ? (
              <div>
                <dt className="inline text-text-3">Model band </dt>
                <dd className="inline font-tabular text-text">
                  covered {pct(band.coverage)} (n={band.n})
                </dd>
              </div>
            ) : null}

            {mape.n > 0 ? (
              <div className="sm:col-span-2">
                <dt className="inline text-text-3">Month-end error </dt>
                <dd className="inline font-tabular text-text">
                  median {pct(mape.median)}, p90 {pct(mape.p90)}
                </dd>
              </div>
            ) : null}
          </dl>
        )}

        {v3 ? (
          <div className="mt-2 border-t border-card-bd pt-2 text-xs text-text-2">
            <span className="text-text-3">Lead-conditional data </span>
            <span className="font-tabular text-text-2">
              ρ̂ {(v3.rho ?? 0).toFixed(2)}, {v3.lead_count} leads, {v3.cell_count} cells, typical
              ±{((v3.rep_rel_half_width ?? 0) * 100).toFixed(1)}%
            </span>{" "}
            <span className="text-text-3">(diagnostics)</span>
          </div>
        ) : null}

        {adaptive ? (
          <div className="mt-2 border-t border-card-bd pt-2 text-xs text-text-2">
            <span className="text-text-3">Adaptive calibration </span>
            <span className="font-tabular text-text-2">
              band multiplier {(adaptive.k_adapt ?? 0).toFixed(2)}×, coverage{" "}
              {pct(adaptive.coverage_ewma)} (n={adaptive.n_obs ?? 0})
            </span>
            {adaptive.point_bias != null && adaptive.correction_factor != null ? (
              <span className="ml-2 font-tabular text-text-2">
                · bias {signedPct(adaptive.point_bias)} →{" "}
                ×{adaptive.correction_factor.toFixed(2)} correction
              </span>
            ) : null}
          </div>
        ) : null}
      </div>

      {/* Per-budget table */}
      {budgets.length > 0 ? (
        <div className="overflow-x-auto rounded-card border border-card-bd">
          <table className="data-table min-w-full text-sm">
            <thead>
              <tr>
                <th>Budget</th>
                <th className="text-right">Coverage (n)</th>
                <th>Range type</th>
                <th className="text-right">MAPE median / p90</th>
              </tr>
            </thead>
            <tbody>
              {budgets.map((b) => {
                const bc = (b.report || {}).conformal || {};
                const bm = (b.report || {}).projection_mape || {};
                return (
                  <tr key={b.budget_id}>
                    <td className="text-text">{b.name}</td>
                    <td className="text-right font-tabular text-text">
                      {bc.n === 0 ? "—" : `${pct(bc.coverage)} (${bc.n})`}
                    </td>
                    <td className="text-text-2">
                      {bc.n === 0 ? "—" : (RANGE_LABEL_TEXT[bc.label] || bc.label || "—")}
                    </td>
                    <td className="text-right font-tabular text-text">
                      {bm.n > 0 ? `${pct(bm.median)} / ${pct(bm.p90)}` : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
};

CalibrationPanel.propTypes = {
  report: PropTypes.shape({
    window_months: PropTypes.number,
    total: PropTypes.shape({
      conformal: PropTypes.shape({
        coverage: PropTypes.number,
        n: PropTypes.number,
        label: PropTypes.string,
      }),
      band: PropTypes.shape({
        coverage: PropTypes.number,
        n: PropTypes.number,
      }),
      projection_mape: PropTypes.shape({
        mean: PropTypes.number,
        median: PropTypes.number,
        p90: PropTypes.number,
        n: PropTypes.number,
      }),
    }),
    v3: PropTypes.shape({
      rho: PropTypes.number,
      lead_count: PropTypes.number,
      cell_count: PropTypes.number,
      rep_rel_half_width: PropTypes.number,
    }),
    adaptive: PropTypes.shape({
      k_adapt: PropTypes.number,
      coverage_ewma: PropTypes.number,
      n_obs: PropTypes.number,
      point_bias: PropTypes.number,
      correction_factor: PropTypes.number,
    }),
    budgets: PropTypes.arrayOf(
      PropTypes.shape({
        budget_id: PropTypes.number,
        name: PropTypes.string,
        report: PropTypes.shape({
          conformal: PropTypes.shape({
            coverage: PropTypes.number,
            n: PropTypes.number,
            label: PropTypes.string,
          }),
          projection_mape: PropTypes.shape({
            mean: PropTypes.number,
            median: PropTypes.number,
            p90: PropTypes.number,
            n: PropTypes.number,
          }),
        }),
      })
    ),
  }),
};

CalibrationPanel.defaultProps = {
  report: undefined,
};

export default CalibrationPanel;
